// GroupChip.jsx

import React from "react";
import { Chip } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";

const hexToRgb = (hex) => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
    if (!m) return { r: 0, g: 0, b: 0 };
    return {
        r: parseInt(m[1], 16),
        g: parseInt(m[2], 16),
        b: parseInt(m[3], 16),
    };
};

const getContrastText = (hex) => {
    const { r, g, b } = hexToRgb(hex);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128 ? "#000" : "#fff";
};

/*
  Props:
  - nombre: string (required)
  - icon: string emoji or short text (optional)
  - color: hex string, e.g. "#4caf50" (default "#999999")
  - deletable: boolean to show delete icon (default false)
  - onDelete: function when delete is clicked (optional)
  - onClick: function when chip is clicked (optional)
  - size: "small" | "medium" (default "small")
  - variant: "filled" | "outlined" (default "filled")
  - principal: boolean, show filled star when true (used in PersonaDrawer)
  - showPrincipalIcon: boolean, whether to render the star icon (used in PersonaDrawer)
  - selected, disabled, ...chipProps: passthrough to MUI Chip
*/

const GroupChip = ({
    nombre,
    icon,
    color = "#999999",
    deletable = false,
    onDelete,
    onClick,
    size = "small",
    variant = "filled",
    selected = false,
    disabled = false,
    principal = false,
    showPrincipalIcon = false,
    ...chipProps
}) => {
    const bg = color;
    const fg = getContrastText(bg);

    const deleteHandler = deletable ? onDelete : undefined;
    const label = `${icon || ""} ${nombre}`.trim();

    const chipIcon = showPrincipalIcon ? (
        principal ? (
            <StarIcon fontSize="small" />
        ) : (
            <StarBorderIcon fontSize="small" />
        )
    ) : undefined;

    return (
        <Chip
            label={label}
            icon={chipIcon}
            onDelete={deleteHandler}
            onClick={onClick}
            disabled={disabled}
            size={size}
            variant={variant}
            sx={{
                bgcolor: variant === "filled" ? bg : "transparent",
                color: variant === "filled" ? fg : "inherit",
                border: "1px solid rgba(0,0,0,0.15)",
                ...(selected && { boxShadow: "0 0 0 2px rgba(0,0,0,0.12)" }),
                "& .MuiChip-deleteIcon": { color: fg, opacity: 0.8 },
            }}
            {...chipProps}
        />
    );
};

export default GroupChip;
